/* eslint-disable */
// ─── UNIFIED SCENARIO ENGINE ──────────────────────────────────────────────────
// One engine for both Scenario tabs and both return modes.
//
//   runScenario({
//     startBalance, ageYE, currentYear,
//     returns: { mode: "assumed", equityRate, bondRate, bondPct, dividendYield (default 0.02), projectToYearEnd }
//               (legacy: { mode: "assumed", baseRate, postRate } = single sleeve, no bond accounting)
//           | { mode: "hist", allocs: {spx,djia,ndx,eafe,agg} (% summing to 100),
//               yearBegin, yearEnd, flipped },
//
//   BOND-SLEEVE ACCOUNTING: the IRA is held as dollar sleeves (equity/bond in
//   assumed mode; one per index in hist mode). SPIA premiums and the FIA slice
//   are drawn from the fixed-income sleeve first (bond / AGG), then pro rata.
//   The sleeves are never re-bought back to the original mix, so the post-
//   purchase portfolio is tilted toward equity by exactly what was annuitized.
//   Baseline holds the same sleeves untouched. Withdrawals are pro rata.
//     spia:   { allocationPct, term, rate, arbitrageRate },
//     fia:    null | { allocationPct, term, participation, cap, rebalance, terminateYear },
//     horizon                       // years; hist mode derives it from the year range
//   })
//
// Rules (settled 9/11/2026):
//   • Historical years supply the RETURN SEQUENCE only; the client's current age
//     and balance are always the anchor.
//   • Baseline is a no-annuity premise — full RMD withdrawn every year.
//   • SPIA premium is bought in year 1 out of the IRA; its FMV amortizes
//     straight-line to zero at the end of the term and is INCLUDED in the RMD
//     divisor base (prior year-end FMV), net of the arbitrage rate.
//   • FIA slice (springing): carved from the post-premium IRA as a % of it.
//     FMV for the RMD divisor is held at basis between anniversaries; on each
//     anniversary the credit is  max(0, min(cap, participation × (index growth − 1)))
//     applied to basis (net of withdrawals), then the term re-arms.
//     Index = S&P 500 Price Index (hist mode) or the post-SPIA rate (assumed mode).
//   • RMD shortfall above SPIA income is withdrawn Managed → FIA.
//   • SPIA premiums are never sourced from the FIA.
//   • Rebalance (if on) happens only at a credit. Termination (if set) happens
//     at an anniversary: the credited value moves to Managed.
//   • Inherited IRAs are NOT part of either path — they are excluded upstream.
//   • Rebalance (holistic, user-dated): at the end of each year in
//     spia.rebalanceYears (1-based projection years), re-run the original rule:
//     new tranche income = allocationPct × next year's projected RMD − income
//     still paying next year; term and rate as entered. Premium from Managed,
//     except on an FIA credit year where the credited FIA is drawn first.
//   • Every tranche carries two values: regulatory FMV (straight-line to zero,
//     for display) and economic value (PV of remaining payments at its own
//     internal rate), which is what total wealth counts.

import { getDistributionPeriod } from "./tables";
import { HIST } from "./hist";

export function calculateSPIAPremium(allocation, spiaRate, spiaTerm) {
  if (!allocation || !spiaTerm) return 0;
  const monthlyRate = spiaRate / 12;
  const n = spiaTerm * 12;
  if (monthlyRate === 0) return allocation;
  return allocation / ((monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1)) / 12;
}

function blendReturn(allocs, year) {
  const a = allocs || {};
  return (
    ((a.spx  || 0) / 100) * (HIST.spx[year]  || 0) +
    ((a.djia || 0) / 100) * (HIST.djia[year] || 0) +
    ((a.ndx  || 0) / 100) * (HIST.ndx[year]  || 0) +
    ((a.eafe || 0) / 100) * (HIST.eafe[year] || 0) +
    ((a.agg  || 0) / 100) * (HIST.agg[year]  || 0)
  ) / 100;
}

export function runScenario(cfg) {
  const { startBalance = 0, ageYE, currentYear, returns, spia } = cfg;
  const fia = cfg.fia && cfg.fia.allocationPct > 0 && cfg.fia.term > 0 ? cfg.fia : null;
  const mode = returns?.mode === "hist" ? "hist" : "assumed";

  // ── return sequence ─────────────────────────────────────────────────────────
  let horizon = cfg.horizon || 10;
  let retYears = [];
  if (mode === "hist") {
    const yb = returns.yearBegin, ye = returns.yearEnd;
    for (let y = yb; y <= ye; y++) retYears.push(y);
    if (returns.flipped) retYears.reverse();
    horizon = retYears.length;
  }
  // ── sleeves ────────────────────────────────────────────────────────────────
  const legacy = mode === "assumed" && returns?.equityRate == null;
  let sleeveKeys, fixedKey, sleeveWeights, sleeveRate;
  if (mode === "hist") {
    const a = returns.allocs || {};
    sleeveKeys = ["spx", "djia", "ndx", "eafe", "agg"].filter(k => (a[k] || 0) > 0);
    const tot = sleeveKeys.reduce((s, k) => s + a[k], 0) || 1;
    sleeveWeights = Object.fromEntries(sleeveKeys.map(k => [k, a[k] / tot]));
    fixedKey = "agg";
    sleeveRate = (k, i) => (HIST[k][retYears[i]] || 0) / 100;
  } else if (legacy) {
    sleeveKeys = ["eq"]; sleeveWeights = { eq: 1 }; fixedKey = null;
    const baseRate = returns?.baseRate ?? 0.05, postRate = returns?.postRate ?? baseRate;
    sleeveRate = (k, i, which) => which === "base" ? baseRate : postRate;
  } else {
    const bp = Math.min(1, Math.max(0, returns.bondPct ?? 0.4));
    sleeveKeys = ["eq", "bd"]; sleeveWeights = { eq: 1 - bp, bd: bp }; fixedKey = "bd";
    sleeveRate = (k) => (k === "bd" ? (returns.bondRate ?? 0.04) : (returns.equityRate ?? 0.08));
  }
  const baseRate = legacy ? (returns?.baseRate ?? 0.05)
    : mode === "assumed" ? sleeveKeys.reduce((s, k) => s + sleeveWeights[k] * sleeveRate(k, 0), 0) : 0;
  // Assumed-mode FIA index = equity sleeve rate less an assumed dividend yield (FIAs credit the PRICE index).
  const divYield = returns?.dividendYield ?? 0.02;
  const indexReturn = (i) => mode === "hist" ? (HIST.spxpi[retYears[i]] || 0) / 100 : (legacy ? (returns?.postRate ?? baseRate) : Math.max(0, (returns.equityRate ?? 0.08) - divYield));

  const mkSleeve = (total) => Object.fromEntries(sleeveKeys.map(k => [k, total * sleeveWeights[k]]));
  const sTotal = (sl) => sleeveKeys.reduce((s, k) => s + sl[k], 0);
  const sGrow = (sl, i, which) => { sleeveKeys.forEach(k => { sl[k] = Math.max(0, sl[k] * (1 + sleeveRate(k, i, which))); }); };
  const sWeightedRate = (sl, i, which) => { const t = sTotal(sl); return t > 0 ? sleeveKeys.reduce((s, k) => s + sl[k] / t * sleeveRate(k, i, which), 0) : 0; };
  const sProRata = (sl, amt) => { const t = sTotal(sl); const take = Math.min(amt, t); if (t > 0) sleeveKeys.forEach(k => { sl[k] -= take * sl[k] / t; }); return take; };
  const sFixedFirst = (sl, amt) => {                       // fixed-income sleeve first, then pro rata
    let left = amt, fromFixed = 0;
    if (fixedKey && sl[fixedKey] > 0) { fromFixed = Math.min(left, sl[fixedKey]); sl[fixedKey] -= fromFixed; left -= fromFixed; }
    const rest = left > 0 ? sProRata(sl, left) : 0;
    return { taken: fromFixed + rest, fromFixed };
  };
  const sAdd = (sl, amt) => { const t = sTotal(sl); if (t > 0) sleeveKeys.forEach(k => { sl[k] += amt * sl[k] / t; }); else sl[sleeveKeys[0]] += amt; };

  const yearReturns = (i) => ({ retYear: mode === "hist" ? retYears[i] : null, index: indexReturn(i) });

  // ── year-1 anchor ───────────────────────────────────────────────────────────
  const divisor = getDistributionPeriod(ageYE);
  let projectedValue = startBalance;
  if (mode === "assumed" && returns?.projectToYearEnd !== false) {
    const monthlyRate = Math.pow(1 + baseRate, 1 / 12);
    const monthsRemain = 12 - (new Date().getMonth() + 1);
    projectedValue = startBalance * Math.pow(monthlyRate, monthsRemain);
  }
  const rmd = divisor > 0.001 ? projectedValue / divisor : 0;

  const spiaTerm = spia?.term || 0;
  const spiaRate = spia?.rate || 0;
  const allocationPct = spia?.allocationPct || 0;
  const arbitrageRate = spia?.arbitrageRate || 0;
  const allocation = rmd * allocationPct;
  const premium = calculateSPIAPremium(allocation, spiaRate, spiaTerm);
  const rebalanceYears = new Set((spia?.rebalanceYears || []).map(n => Math.round(n)).filter(n => n >= 1));

  // ── SPIA tranches ──────────────────────────────────────────────────────────
  // startIdx: first projection index that receives income; pays for spiaTerm years.
  const tranches = [];
  const addTranche = (annualIncome, prem, startIdx) => {
    tranches.push({ id: tranches.length + 1, annualIncome, premium: prem, startIdx, endIdx: startIdx + spiaTerm - 1 });
  };
  if (premium > 0) addTranche(allocation, premium, 0);
  const trIncome = (t, i) => (i >= t.startIdx && i <= t.endIdx) ? t.annualIncome : 0;
  const trFMV = (t, i) => {                       // regulatory, end of year i
    if (i < t.startIdx - 1) return 0;
    const elapsed = i - t.startIdx + 1;           // years paid through end of year i
    return Math.max(0, t.premium * (1 - elapsed / spiaTerm));
  };
  const trPV = (t, i) => {                        // economic, end of year i: PV of remaining monthly payments
    const monthsLeft = Math.max(0, (t.endIdx - i) * 12);
    if (monthsLeft === 0) return 0;
    const mr = spiaRate / 12, pmt = t.annualIncome / 12;
    return mr > 0 ? pmt * (1 - Math.pow(1 + mr, -monthsLeft)) / mr : pmt * monthsLeft;
  };

  // ── FIA config ──────────────────────────────────────────────────────────────
  const fiaTerm = fia ? Math.round(fia.term) : 0;
  const fiaPar = fia ? (fia.participation ?? 1) : 1;
  const fiaCap = fia && fia.cap != null && fia.cap > 0 ? fia.cap : null;   // term cap, decimal
  const fiaRebalance = !!(fia && fia.rebalance);
  const fiaTerminateYear = fia && fia.terminateYear > 0 ? Math.round(fia.terminateYear) : 0;
  const creditPct = (idxFactor) => {
    let c = fiaPar * (idxFactor - 1);
    if (fiaCap != null) c = Math.min(c, fiaCap);
    return Math.max(0, c);
  };

  // ── state ───────────────────────────────────────────────────────────────────
  const baseSl = mkSleeve(projectedValue);
  const manSl = mkSleeve(projectedValue);          // managed sleeves of the 204 IRA
  let spiaValue = projectedValue;                  // economic total of the 204 IRA (managed + FIA)
  let managed = 0, fiaBasis = 0, fiaFMV = 0, fiaIdx = 1, fiaShare = 0, fiaAmount = 0, fiaActive = false;
  let premiumFromFixed = 0, fiaFromFixed = 0;
  const baseline = [], spiaProj = [];

  for (let i = 0; i < horizon; i++) {
    const year = currentYear + i + 1;
    const age = ageYE + i + 1;
    const div = getDistributionPeriod(age);
    const r = yearReturns(i);

    // ── Baseline ──
    const baseValueStart = sTotal(baseSl);
    const baseRMD = div > 0.001 ? baseValueStart / div : 0;
    const baseRet = sWeightedRate(baseSl, i, "base");
    sProRata(baseSl, baseRMD); sGrow(baseSl, i, "base");
    baseline.push({ Year: year, Age: age, RetYear: r.retYear, Return: baseRet, IRAValue: sTotal(baseSl), RMD: baseRMD, Divisor: div,
      BondPct: fixedKey && sTotal(baseSl) > 0 ? baseSl[fixedKey] / sTotal(baseSl) : 0 });

    // ── Sec. 204 ──
    if (i === 0) {
      premiumFromFixed = sFixedFirst(manSl, premium).fromFixed;   // premium out of the bond sleeve first
      spiaValue = sTotal(manSl);
      if (fiaTerm) {
        fiaAmount = Math.max(0, spiaValue) * fia.allocationPct;
        fiaFromFixed = sFixedFirst(manSl, fiaAmount).fromFixed;   // FIA slice also displaces bonds first
        fiaBasis = fiaAmount; fiaFMV = fiaAmount; fiaIdx = 1; fiaActive = fiaAmount > 0;
        fiaShare = spiaValue > 0 ? fiaAmount / spiaValue : 0;
      }
      managed = sTotal(manSl);
    }
    const spiaIncome = tranches.reduce((s, t) => s + trIncome(t, i), 0);
    const spiaFMVPrior = tranches.reduce((s, t) => s + (i === 0 ? t.premium : trFMV(t, i - 1)), 0) * (1 - arbitrageRate);
    const rmdBase = (fiaActive ? managed + fiaFMV : managed) + spiaFMVPrior;   // annuity FMV aggregates into the divisor base
    const spiaRMD = div > 0.001 ? rmdBase / div : 0;
    const spiaWithdrawal = Math.max(0, spiaRMD - spiaIncome);

    // withdrawals: Managed (pro rata across sleeves) first, then FIA
    const fromManaged = Math.min(spiaWithdrawal, Math.max(0, managed));
    const fromFIA = fiaActive ? Math.min(spiaWithdrawal - fromManaged, Math.max(0, fiaBasis)) : 0;
    const manRet = sWeightedRate(manSl, i, "post");
    sProRata(manSl, fromManaged); sGrow(manSl, i, "post");
    managed = sTotal(manSl);

    let sprung = false, creditRate = 0, forfeited = 0;
    if (fiaActive) {
      fiaBasis = Math.max(0, fiaBasis - fromFIA);
      fiaFMV = Math.max(0, fiaFMV - fromFIA);
      fiaIdx *= (1 + r.index);
      if ((i + 1) % fiaTerm === 0) {                         // anniversary
        sprung = true;
        creditRate = creditPct(fiaIdx);
        const credited = fiaBasis * (1 + creditRate);
        if (fiaTerminateYear && (i + 1) >= fiaTerminateYear) {
          sAdd(manSl, credited); managed = sTotal(manSl);      // termination → Managed (pro rata)
          fiaBasis = 0; fiaFMV = 0; fiaActive = false;
        } else if (fiaRebalance) {
          const total = managed + credited;
          const target = total * fiaShare;
          if (target > credited) sFixedFirst(manSl, target - credited); else sAdd(manSl, credited - target);
          managed = sTotal(manSl); fiaBasis = target; fiaFMV = target;
        } else {
          fiaBasis = credited; fiaFMV = credited;
        }
        fiaIdx = 1;
      }
    }
    // ── holistic rebalance at end of year (i+1) ──
    let rebalance = null;
    if (rebalanceYears.has(i + 1) && i + 1 < horizon) {
      const nextDiv = getDistributionPeriod(age + 1);
      const nextBase = (fiaActive ? managed + fiaFMV : managed) + tranches.reduce((s, t) => s + trFMV(t, i), 0) * (1 - arbitrageRate);
      const nextRMD = nextDiv > 0.001 ? nextBase / nextDiv : 0;
      const existing = tranches.reduce((s, t) => s + trIncome(t, i + 1), 0);
      const targetIncome = Math.max(0, allocationPct * nextRMD - existing);
      let prem = calculateSPIAPremium(targetIncome, spiaRate, spiaTerm);
      let fromFIA = 0, fromMan = 0;
      if (prem > 0) {
        if (sprung && fiaActive) { fromFIA = Math.min(prem, fiaBasis); fiaBasis -= fromFIA; fiaFMV = Math.max(0, fiaFMV - fromFIA); }
        fromMan = sFixedFirst(manSl, prem - fromFIA).taken; managed = sTotal(manSl);   // bonds first
        const funded = fromFIA + fromMan;
        const income = prem > 0 ? targetIncome * (funded / prem) : 0;   // scale down if short
        if (funded > 0) addTranche(income, funded, i + 1);
        rebalance = { year: i + 1, targetIncome: income, premium: funded, fromFIA, fromManaged: fromMan, nextRMD };
      } else {
        rebalance = { year: i + 1, targetIncome: 0, premium: 0, fromFIA: 0, fromManaged: 0, nextRMD };
      }
    }

    // economic value of the FIA between anniversaries = would-be credit to date
    const fiaEcon = fiaActive ? fiaBasis * (1 + creditPct(fiaIdx)) : 0;
    spiaValue = managed + fiaEcon;

    const spiaFMV = tranches.reduce((s, t) => s + trFMV(t, i), 0);
    const spiaPV = tranches.reduce((s, t) => s + trPV(t, i), 0);
    const trancheRows = tranches.map(t => ({ id: t.id, income: trIncome(t, i), fmv: trFMV(t, i), pv: trPV(t, i) }));
    const trancheFlat = {};
    trancheRows.forEach(t => { trancheFlat[`T${t.id}Income`] = t.income; trancheFlat[`T${t.id}FMV`] = t.fmv; trancheFlat[`T${t.id}PV`] = t.pv; });

    spiaProj.push({
      Year: year, Age: age, RetYear: r.retYear, Return: manRet, IndexReturn: r.index,
      BondPct: fixedKey && managed > 0 ? manSl[fixedKey] / managed : 0,
      IRAValue: spiaValue, RMD: spiaRMD,
      SPIAIncome: spiaIncome, SPIAFMV: spiaFMV * (1 - arbitrageRate), SPIAPV: spiaPV,
      Tranches: trancheRows, ...trancheFlat, Rebalance: rebalance,
      AdditionalRMD: spiaWithdrawal,
      TotalSPIARMD: spiaIncome + spiaWithdrawal,
      Divisor: div,
      RMDBase: rmdBase,
      ManagedValue: managed,
      FIAValue: fiaEcon,
      FIAFMV: fiaActive ? fiaFMV : 0,
      FIACredit: sprung ? creditRate : null,
      Sprung: sprung,
    });
  }

  return {
    baseline, spiaProj, premium, allocation, projectedValue, rmd, divisor, horizon, mode,
    baseRate, premiumFromFixed, fiaFromFixed,
    sleeves: { keys: sleeveKeys, weights: sleeveWeights, fixedKey },
    tranches: tranches.map(t => ({ ...t, term: spiaTerm, rate: spiaRate })),
    fia: fiaTerm ? { amount: fiaAmount, allocationPct: fia.allocationPct, term: fiaTerm, participation: fiaPar, cap: fiaCap, rebalance: fiaRebalance, terminateYear: fiaTerminateYear || null } : null,
  };
}

// ─── Back-compat wrapper (frontier + prospect reports call this signature) ────
export function calculateSPIAProjections(iraAssets, returnRate, ageYE, currentYear,
  allocationPct, spiaTerm, spiaRate, arbitrageRate = 0, postSpiaReturnRate = null, fia = null, rebalanceYears = []) {
  return runScenario({
    startBalance: iraAssets, ageYE, currentYear,
    returns: { mode: "assumed", baseRate: returnRate, postRate: postSpiaReturnRate !== null ? postSpiaReturnRate : returnRate },
    spia: { allocationPct, term: spiaTerm, rate: spiaRate, arbitrageRate, rebalanceYears },
    fia,
    horizon: 10,
  });
}

// ─── Summary helpers ──────────────────────────────────────────────────────────
export function summarize(result) {
  if (!result) return null;
  const last = result.spiaProj.length - 1;
  const baseRMD = result.baseline.reduce((s, r) => s + r.RMD, 0);
  const spiaRMD = result.spiaProj.reduce((s, r) => s + r.TotalSPIARMD, 0);
  const baseEnd = result.baseline[last]?.IRAValue || 0;
  const spiaEnd = result.spiaProj[last]?.IRAValue || 0;
  const spiaPVEnd = result.spiaProj[last]?.SPIAPV || 0;
  return {
    baseRMD, spiaRMD, rmdDelta: spiaRMD - baseRMD,
    baseEnd, spiaEnd, endDelta: spiaEnd - baseEnd,
    spiaPVEnd,
    baseWealth: baseEnd + baseRMD,
    spiaWealth: spiaEnd + spiaPVEnd + spiaRMD,
    wealthDelta: (spiaEnd + spiaPVEnd + spiaRMD) - (baseEnd + baseRMD),
  };
}
